import './CAPageForm.less';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { injectIntl, intlShape, FormattedHTMLMessage, FormattedMessage } from 'react-intl';
import { reduxForm, getFormSyncErrors, submit, Form, formValueSelector } from 'redux-form';
import DynamicIframe from '../../../../shared-components/DynamicIframe/DynamicIframe';
import _ from 'lodash';
import StickyFooter from '../../../../shared-components/StickyFooter/StickyFooter';
import Waypoint from 'react-waypoint';

import AgreementCheckbox from '../../../components/AgreementCheckbox/AgreementCheckbox';
import Button, { BUTTON_STYLE } from '../../../../shared-components/Button/Button';
import { CREDIT_TYPE, CA_FORM_ID, FLOW_TYPE, SECCI_FORM_ID } from '../../../../framework/App/AppConstants';
import { spinnerOpen } from '../../../../framework/components/FullPageSpinner/FullPageSpinnerActions';
import { submitContractAgreement } from '../CAPageActions';
import CancelUrl, { COMPONENT_STYLE } from '../../../components/CancelUrl/CancelUrl';
import { showEscapeHatch } from '../../../components/EscapeHatch/EscapeHatchActions';
import { isXOFlow, popupWithHTML } from '../../../../framework/App/AppUtils';
import PageHeaderText from '../../../../shared-components/Typography/PageHeaderText';

const LOCALE_PREFIX = 'GB/pages/CAPage/CAPage';

export class CAPageForm extends Component {
    static propTypes = {
        agreeToReadCAChecked: PropTypes.bool.isRequired,
        formValues: PropTypes.object,
        formSyncErrors: PropTypes.object,
        creditType: PropTypes.string,
        cart: PropTypes.shape({
            gross_total_amount: PropTypes.shape({
                value: PropTypes.string
            })
        }),
        onFormSubmit: PropTypes.func.isRequired,
        htmlContract: PropTypes.string.isRequired,
        handleSubmit: PropTypes.func.isRequired,
        onClickContinue: PropTypes.func.isRequired,
        showEscapeHatch: PropTypes.func.isRequired,
        flow: PropTypes.string,
        agreeToReadSecciChecked: PropTypes.bool,
        submitFailed: PropTypes.bool,
        // Comes from injectIntl HOC
        intl: intlShape.isRequired,

        // Comes from reduxForm HOC
        dirty: PropTypes.bool, // Has form been touched
        submitting: PropTypes.bool // Redux inject this to indicate if the form submit is still in progress
    };

    constructor() {
        super();
        this.state = {
            fixToBottom: true
        };
    }

    handleWaypointEnter = () => this.setState({ fixToBottom: false });

    handleWaypointLeave = () => this.setState({ fixToBottom: true });

    showCAPrintPopup = event => {
        event.preventDefault();
        popupWithHTML({
            title: 'CreditContract',
            html: this.props.htmlContract,
        }).print();
    };

    handleShowEscapeHatch = () => {
        this.props.showEscapeHatch();
    };

    render() {
        const {
            handleSubmit,
            htmlContract,
            onFormSubmit,
            onClickContinue,
            intl,
            flow,
            agreeToReadCAChecked,
            agreeToReadSecciChecked,
            submitFailed,
            submitting
        } = this.props;
        const { fixToBottom } = this.state;

        const isEsignMobileAlert = () => {
            return (!agreeToReadCAChecked || !agreeToReadSecciChecked) && submitFailed;
        };

        let agreementpdfLink;
        if (flow !== FLOW_TYPE.VENICE) {
            agreementpdfLink = (
                <a href="#"
                    className="print-html-link"
                    data-automation-id="print-mobile"
                    name="print-html-link"
                    onClick={this.showCAPrintPopup}
                >
                    {intl.formatMessage({ id: `${LOCALE_PREFIX}.download` })}
                </a>);
        } else {
            agreementpdfLink = (
                <a href="creditContract"
                    className="print-html-link"
                    data-automation-id="print-mobile"
                    name="print-html-link"
                >
                    {intl.formatMessage({ id: `${LOCALE_PREFIX}.download` })}
                </a>);
        }

        const continueButtonText = <FormattedMessage id={`${LOCALE_PREFIX}.agreeToTerms`} />;

        const mobileAlert = () => (
            <div className="show-for-mobile CAPageForm-mobile-alert">
                <div className="vx_alert vx_alert-critical" role="alert" aria-live="assertive">
                    <p className="vx_alert-text">
                        <FormattedMessage id={`${LOCALE_PREFIX}.mobileAlert`} />
                    </p>
                </div>
            </div>
        );

        return (
            <Form
                className="proceed"
                id="CAPageForm"
                onSubmit={handleSubmit(onFormSubmit)}
            >
                <div id="creditContractVisibleContainer">
                    <div id="creditContractHeader">
                        <PageHeaderText>{intl.formatMessage({ id: `${LOCALE_PREFIX}.approvedHeader` })}</PageHeaderText>
                        <p className="primary read-and-agree-text">
                            <FormattedHTMLMessage id={`${LOCALE_PREFIX}.readAndAgree`} />
                        </p>
                        <p className="CAPageForm-mobile-download primary print-content show-for-mobile">
                            <span className="download-icon" />
                            {agreementpdfLink}
                        </p>
                        <p className="CAPageForm-desktop-print primary print-content hide-for-mobile">
                            <span className="print-icon" />
                            <a href="#"
                                data-automation-id="print"
                                className="print-html-link"
                                name="print-html-link"
                                onClick={this.showCAPrintPopup}
                            >
                                {intl.formatMessage({ id: `${LOCALE_PREFIX}.printCopy` })}
                            </a>
                        </p>
                    </div>

                    <div className="outline-frame">
                        {isEsignMobileAlert() && mobileAlert()}
                        <div className="contract-fade" />
                        <div className="outline-body">
                            <DynamicIframe
                                htmlString={htmlContract}
                                autoHeight={true}
                                extraHeight={80}
                                scrolling={'no'} //iphone fix for iframe: this helps format the iframe to fit within the iphone window.
                                className="credit-contract-iframe"
                            />
                        </div>
                    </div>

                    <StickyFooter fixToBottom={fixToBottom} fadedBackground={true}>
                        <div className="agree-secci">
                            <AgreementCheckbox
                                agreementType="secci"
                                agreementChecked={agreeToReadSecciChecked}
                                scrollToBottomOnCheck={false}
                            />
                        </div>
                        <div className="agree-credit">
                            <AgreementCheckbox
                                agreementType="credit"
                                agreementChecked={agreeToReadCAChecked}
                            />
                        </div>
                    </StickyFooter>
                    <Waypoint
                        onEnter={this.handleWaypointEnter}
                        onLeave={this.handleWaypointLeave}
                    />
                </div>
                <div className="gbCredit-continueButton-wrapper paypal-contract-buttons">
                    <Button
                        data-automation-id="creditContractAgreeToTermsGB"
                        btnType="button"
                        btnStyle={BUTTON_STYLE.PP_PRIMARY}
                        className="creditContractAgreeToTermsGB"
                        onClick={onClickContinue}
                        id="creditContractAgreeToTermsGB"
                        name="creditContractAgreeToTermsGB"
                        disabled={submitting}
                    >
                        {continueButtonText}
                    </Button>
                    <CancelUrl
                        componentType={COMPONENT_STYLE.LINK_BUTTON}
                        customOnClick={this.handleShowEscapeHatch}
                        fptiEvent={{
                            stateName: 'apply_pp_credit_contractpage',
                            transitionName: 'cancel_apply_pp_credit_contractpage'
                        }}
                    />
                </div>
            </Form>
        );
    }
}

export const mapDispatchToProps = dispatch => ({
    onClickContinue() {
        dispatch(submit(CA_FORM_ID));
    },
    onFormSubmit(formValues) {
        dispatch(spinnerOpen());
        // submitContractAgreement() returns a promise -
        // use `return` to pass it up to the redux-form HOC
        return dispatch(submitContractAgreement(formValues));
    },
    showEscapeHatch() {
        dispatch(showEscapeHatch());
    }
});

export const mapStateToProps = state => ({
    agreeToReadCAChecked: formValueSelector(CA_FORM_ID)(state, 'agreeToReadCredit') === true,
    formValues: _.get(state, `form.${CA_FORM_ID}.values`),
    formSyncErrors: getFormSyncErrors(CA_FORM_ID)(state),
    creditType: _.get(state, 'serverData.financingDetails.credit_type', CREDIT_TYPE.CORE),
    cart: _.get(state, 'serverData.cart', {}),
    htmlContract: _.get(state, 'serverData.decisionModel.html_contract', ''),
    flow: _.get(state, 'serverData.flow'),
    agreeToReadSecciChecked: formValueSelector(CA_FORM_ID)(state, 'agreeToReadSecci') === true,
    submitFailed: _.get(state, `form.${CA_FORM_ID}.submitFailed`)
});

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(
    reduxForm({
        form: CA_FORM_ID,
        destroyOnUnmount: false,
        forceUnregisteredOnUnmount: true
    })(CAPageForm)
));
